import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, ArrowLeft, FolderKanban, Eye, EyeOff } from "lucide-react";
import { Field, inputCls, Toggle } from "../forms";
import { adminCreateProject, adminUpdateProject, adminDeleteProject } from "../../lib/api";

const slugify = (s) =>
  s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase()
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Format des blocs : un bloc par ligne, « Titre :: texte »
const blocksToText = (blocks = []) =>
  blocks.map((b) => `${b.titre} :: ${b.texte}`).join("\n");

const textToBlocks = (text) =>
  text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const [titre, ...rest] = l.split("::");
      return { titre: titre.trim(), texte: rest.join("::").trim() || titre.trim() };
    });

const EMPTY = {
  slug: "", titre: "", soustitre: "", tags: "", badge: "",
  img: "", cta_titre: "", cta_url: "", ordre: 0, blocksText: "", published: true,
};

export function ProjectsPanel({ token, projects, onChanged }) {
  const [draft, setDraft] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");

  const startNew = () => {
    setSlugTouched(false);
    setDraft({ ...EMPTY, ordre: (projects[projects.length - 1]?.ordre ?? 0) + 1 });
  };

  const startEdit = (p) => {
    setSlugTouched(true);
    setDraft({
      id: p.id, slug: p.slug, titre: p.titre, soustitre: p.soustitre, tags: p.tags,
      badge: p.badge, img: p.img, cta_titre: p.cta_titre, cta_url: p.cta_url,
      ordre: p.ordre, blocksText: blocksToText(p.blocks), published: p.published,
    });
  };

  const setField = (key) => (e) => {
    const v = e.target.value;
    setDraft((d) => {
      const next = { ...d, [key]: v };
      if (key === "titre" && !d.id && !slugTouched) next.slug = slugify(v);
      return next;
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      slug: draft.slug, titre: draft.titre, soustitre: draft.soustitre, tags: draft.tags,
      badge: draft.badge, img: draft.img, cta_titre: draft.cta_titre, cta_url: draft.cta_url,
      ordre: Number(draft.ordre) || 0, blocks: textToBlocks(draft.blocksText), published: draft.published,
    };
    try {
      if (draft.id) {
        await adminUpdateProject(token, draft.id, payload);
        toast.success("Projet mis à jour, les changements sont en ligne.");
      } else {
        await adminCreateProject(token, payload);
        toast.success(payload.published ? "Projet publié : il est en ligne." : "Brouillon enregistré (non publié).");
      }
      setDraft(null);
      onChanged();
    } catch (err) {
      toast.error(err.message === "request_failed" ? "Impossible d'enregistrer le projet." : err.message);
    } finally {
      setSaving(false);
    }
  };

  const quickToggle = async (p) => {
    setBusyId(p.id);
    try {
      await adminUpdateProject(token, p.id, {
        slug: p.slug, titre: p.titre, soustitre: p.soustitre, tags: p.tags, badge: p.badge,
        img: p.img, cta_titre: p.cta_titre, cta_url: p.cta_url, ordre: p.ordre,
        blocks: p.blocks, published: !p.published,
      });
      toast.success(p.published ? "Projet retiré du site (brouillon)." : "Projet publié : il est en ligne.");
      onChanged();
    } catch {
      toast.error("Impossible de changer la publication.");
    } finally {
      setBusyId("");
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Supprimer définitivement « ${p.titre} » ? Cette action est irréversible.`)) return;
    setBusyId(p.id);
    try {
      await adminDeleteProject(token, p.id);
      toast.success("Projet supprimé.");
      onChanged();
    } catch {
      toast.error("Suppression impossible.");
    } finally {
      setBusyId("");
    }
  };

  if (draft) {
    return (
      <form data-testid="project-editor-form" onSubmit={onSubmit}
        className="max-w-3xl rounded-[1.75rem] border border-linen bg-shell p-8 md:p-10">
        <button type="button" onClick={() => setDraft(null)} data-testid="project-editor-back"
          className="group inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.2em] text-ash transition-colors duration-300 hover:text-clay">
          <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
          Retour à la liste
        </button>
        <h2 className="mt-6 font-serif text-2xl font-light tracking-tight md:text-3xl">
          {draft.id ? "Modifier" : "Ajouter"} <em className="text-clay">un projet.</em>
        </h2>

        <div className="mt-8 space-y-7">
          <Field label="Titre *" htmlFor="proj-titre">
            <input id="proj-titre" data-testid="project-titre-input" required minLength={2} maxLength={150}
              value={draft.titre} onChange={setField("titre")} placeholder="Ex. : Repères pour l'intime" className={inputCls} />
          </Field>
          <Field label="Sous-titre" htmlFor="proj-soustitre">
            <textarea id="proj-soustitre" data-testid="project-soustitre-input" rows={2} maxLength={300}
              value={draft.soustitre} onChange={setField("soustitre")}
              placeholder="Une phrase d'accroche sous le titre…" className={`${inputCls} resize-none`} />
          </Field>
          <div className="grid gap-7 md:grid-cols-2">
            <Field label="Adresse (slug) *" htmlFor="proj-slug">
              <input id="proj-slug" data-testid="project-slug-input" required minLength={2} maxLength={100}
                pattern="[a-z0-9]+(-[a-z0-9]+)*" value={draft.slug}
                onChange={(e) => { setSlugTouched(true); setField("slug")(e); }}
                placeholder="reperes-pour-l-intime" className={inputCls} />
            </Field>
            <Field label="Ordre d'affichage" htmlFor="proj-ordre">
              <input id="proj-ordre" data-testid="project-ordre-input" type="number" min={0} max={99}
                value={draft.ordre} onChange={setField("ordre")} className={inputCls} />
            </Field>
          </div>
          <div className="grid gap-7 md:grid-cols-2">
            <Field label="Étiquettes (séparées par ·)" htmlFor="proj-tags">
              <input id="proj-tags" data-testid="project-tags-input" maxLength={200}
                value={draft.tags} onChange={setField("tags")}
                placeholder="Santé sexuelle · Innovation" className={inputCls} />
            </Field>
            <Field label="Badge d'état (optionnel)" htmlFor="proj-badge">
              <input id="proj-badge" data-testid="project-badge-input" maxLength={60}
                value={draft.badge} onChange={setField("badge")}
                placeholder="Ex. : Projet à venir" className={inputCls} />
            </Field>
          </div>
          <Field label="Image ou illustration (URL)" htmlFor="proj-img">
            <input id="proj-img" data-testid="project-img-input" maxLength={500}
              value={draft.img} onChange={setField("img")} placeholder="https://…" className={inputCls} />
          </Field>
          <Field label="Contenu (un bloc par ligne : Titre :: texte)" htmlFor="proj-blocks">
            <textarea id="proj-blocks" data-testid="project-blocks-input" rows={6}
              value={draft.blocksText} onChange={setField("blocksText")}
              placeholder={"La mission :: Rendre les services plus accessibles…\nL'objectif :: Offrir un moyen simple de…"}
              className={`${inputCls} resize-y font-mono text-sm leading-relaxed`} />
          </Field>
          <div className="grid gap-7 md:grid-cols-2">
            <Field label="Bouton : texte" htmlFor="proj-cta-titre">
              <input id="proj-cta-titre" data-testid="project-cta-titre-input" maxLength={120}
                value={draft.cta_titre} onChange={setField("cta_titre")}
                placeholder="Ex. : Découvrir Sexualis" className={inputCls} />
            </Field>
            <Field label="Bouton : lien externe (optionnel)" htmlFor="proj-cta-url">
              <input id="proj-cta-url" data-testid="project-cta-url-input" maxLength={300}
                value={draft.cta_url} onChange={setField("cta_url")}
                placeholder="https://… (vide = renvoie vers Contact)" className={inputCls} />
            </Field>
          </div>
          <div className="flex items-center justify-between gap-6 rounded-[1.75rem] border border-linen bg-cream/60 p-6">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-ash">Publication</p>
              <p className="mt-1 text-sm text-smoke" data-testid="project-publish-state">
                {draft.published ? "Visible sur le site dès l'enregistrement" : "Brouillon : invisible sur le site"}
              </p>
            </div>
            <Toggle checked={draft.published} onChange={(v) => setDraft((d) => ({ ...d, published: v }))} testId="project-publish-toggle" />
          </div>
          <button type="submit" data-testid="project-save-button" disabled={saving}
            className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <FolderKanban className="h-4 w-4" />}
            {saving ? "Enregistrement" : draft.id ? "Enregistrer les changements" : "Publier le projet"}
          </button>
        </div>
      </form>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <p className="text-sm text-smoke">
          <strong>{projects.length}</strong> projet(s) · {projects.filter((p) => p.published).length} en ligne
        </p>
        <button onClick={startNew} data-testid="project-new-button"
          className="group inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal">
          <Plus className="h-4 w-4 transition-transform duration-300 group-hover:rotate-90" />
          Nouveau projet
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {projects.length === 0 ? (
          <p className="rounded-[1.75rem] border border-dashed border-linen py-14 text-center font-serif text-xl font-light italic text-smoke">
            Aucun projet pour l'instant. Ajoutez le premier !
          </p>
        ) : projects.map((p, i) => (
          <div key={p.id} data-testid={`admin-projet-${i + 1}`}
            className="flex flex-wrap items-center justify-between gap-4 rounded-[1.75rem] border border-linen bg-shell p-6">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-3">
                <p className="font-serif text-lg font-light leading-snug tracking-tight">{p.titre}</p>
                {p.badge && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-clay/10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] text-clay">
                    {p.badge}
                  </span>
                )}
                <span data-testid={`admin-projet-${i + 1}-status`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.15em] ${
                  p.published ? "bg-sage/60 text-smoke" : "bg-clay/10 text-clay"
                }`}>
                  {p.published ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                  {p.published ? "En ligne" : "Brouillon"}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-ash">
                {p.tags || "sans étiquettes"} · ordre {p.ordre} · /projets#{p.slug}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <button onClick={() => quickToggle(p)} disabled={busyId === p.id} data-testid={`admin-projet-${i + 1}-toggle`}
                className="inline-flex items-center gap-2 rounded-full border border-linen px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay disabled:opacity-60">
                {busyId === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : p.published ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                {p.published ? "Dépublier" : "Publier"}
              </button>
              <button onClick={() => startEdit(p)} data-testid={`admin-projet-${i + 1}-edit`}
                className="inline-flex items-center gap-2 rounded-full border border-linen px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay">
                <Pencil className="h-3.5 w-3.5" />
                Modifier
              </button>
              <button onClick={() => onDelete(p)} disabled={busyId === p.id} data-testid={`admin-projet-${i + 1}-delete`}
                className="inline-flex items-center gap-2 rounded-full border border-linen px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-smoke transition-colors duration-300 hover:border-clay hover:text-clay disabled:opacity-60">
                <Trash2 className="h-3.5 w-3.5" />
                Supprimer
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
